// Binary codec shared with the server (see starfall-protocol/src/codec.rs).
//
// Wire rules: little-endian fixed-width numbers, bool as one byte, string as
// u16 length + UTF-8, bytes as u32 length + raw, uuid as 16 raw bytes,
// vec2/3/4 as packed f32, quat as "smallest three" in 32 bits, color as RGBA8,
// lists as u16 count + elements.

export type Uuid = string;
export type Vec2 = { x: number; y: number };
export type Vec3 = { x: number; y: number; z: number };
export type Vec4 = { x: number; y: number; z: number; w: number };
export type Quat = { x: number; y: number; z: number; w: number };
export type Color = { r: number; g: number; b: number; a: number };

export const NULL_UUID: Uuid = "00000000-0000-0000-0000-000000000000";

export const FRAME_HEADER_LEN = 6;
export const MAX_FRAME_BODY = 8 * 1024 * 1024;
export const DATAGRAM_BUDGET = 1150;

const QUAT_COMPONENT_BITS = 10;
const QUAT_COMPONENT_MAX = (1 << (QUAT_COMPONENT_BITS - 1)) - 1;
const QUAT_MASK = (1 << QUAT_COMPONENT_BITS) - 1;
const QUAT_RANGE = Math.SQRT1_2;

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder("utf-8", { fatal: false });

const HEX: string[] = [];
for (let i = 0; i < 256; i++) HEX.push(i.toString(16).padStart(2, "0"));

export function uuidFromBytes(b: Uint8Array, offset = 0): Uuid {
  const h = (i: number) => HEX[b[offset + i]];
  return (
    h(0) + h(1) + h(2) + h(3) + "-" +
    h(4) + h(5) + "-" +
    h(6) + h(7) + "-" +
    h(8) + h(9) + "-" +
    h(10) + h(11) + h(12) + h(13) + h(14) + h(15)
  );
}

export function uuidToBytes(id: Uuid, out: Uint8Array, offset = 0): void {
  let j = 0;
  for (let i = 0; i < id.length && j < 16; i++) {
    const c = id.charCodeAt(i);
    if (c === 45) continue;
    const hi = hexValue(c);
    const lo = hexValue(id.charCodeAt(++i));
    out[offset + j++] = (hi << 4) | lo;
  }
  while (j < 16) out[offset + j++] = 0;
}

function hexValue(c: number): number {
  if (c >= 48 && c <= 57) return c - 48;
  if (c >= 97 && c <= 102) return c - 87;
  if (c >= 65 && c <= 70) return c - 55;
  return 0;
}

export function isNullUuid(id: Uuid): boolean {
  return id === NULL_UUID || id === "";
}

export function packQuat(q: Quat): number {
  let { x, y, z, w } = q;
  const len = Math.hypot(x, y, z, w) || 1;
  x /= len; y /= len; z /= len; w /= len;
  const comps = [x, y, z, w];
  let largest = 0;
  for (let i = 1; i < 4; i++) {
    if (Math.abs(comps[i]) > Math.abs(comps[largest])) largest = i;
  }
  const sign = comps[largest] < 0 ? -1 : 1;
  let packed = largest << 30;
  let shift = 0;
  for (let i = 0; i < 4; i++) {
    if (i === largest) continue;
    const v = Math.max(-1, Math.min(1, (comps[i] * sign) / QUAT_RANGE));
    const qv = Math.round(v * QUAT_COMPONENT_MAX);
    packed |= (qv & QUAT_MASK) << shift;
    shift += QUAT_COMPONENT_BITS;
  }
  return packed >>> 0;
}

export function unpackQuat(packed: number): Quat {
  const largest = packed >>> 30;
  const comps = [0, 0, 0, 0];
  let shift = 0;
  let sumSq = 0;
  for (let i = 0; i < 4; i++) {
    if (i === largest) continue;
    const raw = (packed >>> shift) & QUAT_MASK;
    const signed = (raw << (32 - QUAT_COMPONENT_BITS)) >> (32 - QUAT_COMPONENT_BITS);
    const v = (signed / QUAT_COMPONENT_MAX) * QUAT_RANGE;
    comps[i] = v;
    sumSq += v * v;
    shift += QUAT_COMPONENT_BITS;
  }
  comps[largest] = Math.sqrt(Math.max(0, 1 - sumSq));
  const len = Math.hypot(comps[0], comps[1], comps[2], comps[3]) || 1;
  return { x: comps[0] / len, y: comps[1] / len, z: comps[2] / len, w: comps[3] / len };
}

export class Writer {
  private buf: Uint8Array;
  private view: DataView;
  private pos = 0;

  constructor(capacity = 256) {
    this.buf = new Uint8Array(capacity);
    this.view = new DataView(this.buf.buffer);
  }

  get length(): number {
    return this.pos;
  }

  bytesWritten(): Uint8Array {
    return this.buf.subarray(0, this.pos);
  }

  /** Copy out the written bytes into a fresh buffer. */
  finish(): Uint8Array {
    return this.buf.slice(0, this.pos);
  }

  reset(): void {
    this.pos = 0;
  }

  private ensure(n: number): void {
    if (this.pos + n <= this.buf.length) return;
    let size = this.buf.length * 2;
    while (size < this.pos + n) size *= 2;
    const next = new Uint8Array(size);
    next.set(this.buf.subarray(0, this.pos));
    this.buf = next;
    this.view = new DataView(next.buffer);
  }

  u8(v: number): void { this.ensure(1); this.buf[this.pos++] = v & 0xff; }
  i8(v: number): void { this.ensure(1); this.view.setInt8(this.pos, v); this.pos += 1; }
  u16(v: number): void { this.ensure(2); this.view.setUint16(this.pos, v, true); this.pos += 2; }
  i16(v: number): void { this.ensure(2); this.view.setInt16(this.pos, v, true); this.pos += 2; }
  u32(v: number): void { this.ensure(4); this.view.setUint32(this.pos, v >>> 0, true); this.pos += 4; }
  i32(v: number): void { this.ensure(4); this.view.setInt32(this.pos, v | 0, true); this.pos += 4; }
  f32(v: number): void { this.ensure(4); this.view.setFloat32(this.pos, v, true); this.pos += 4; }
  f64(v: number): void { this.ensure(8); this.view.setFloat64(this.pos, v, true); this.pos += 8; }

  u64(v: bigint): void {
    this.ensure(8);
    this.view.setBigUint64(this.pos, BigInt.asUintN(64, v), true);
    this.pos += 8;
  }

  i64(v: bigint): void {
    this.ensure(8);
    this.view.setBigInt64(this.pos, BigInt.asIntN(64, v), true);
    this.pos += 8;
  }

  bool(v: boolean): void { this.u8(v ? 1 : 0); }

  string(s: string): void {
    const bytes = textEncoder.encode(s);
    const len = Math.min(bytes.length, 0xffff);
    this.u16(len);
    this.ensure(len);
    this.buf.set(bytes.subarray(0, len), this.pos);
    this.pos += len;
  }

  bytes(b: Uint8Array): void {
    this.u32(b.length);
    this.ensure(b.length);
    this.buf.set(b, this.pos);
    this.pos += b.length;
  }

  uuid(id: Uuid): void {
    this.ensure(16);
    uuidToBytes(id, this.buf, this.pos);
    this.pos += 16;
  }

  vec2(v: Vec2): void { this.f32(v.x); this.f32(v.y); }
  vec3(v: Vec3): void { this.f32(v.x); this.f32(v.y); this.f32(v.z); }
  vec4(v: Vec4): void { this.f32(v.x); this.f32(v.y); this.f32(v.z); this.f32(v.w); }
  quat(q: Quat): void { this.u32(packQuat(q)); }

  color(c: Color): void {
    this.u8(c.r); this.u8(c.g); this.u8(c.b); this.u8(c.a);
  }

  /** Reserve `n` bytes to patch later; returns the offset. */
  reserve(n: number): number {
    this.ensure(n);
    const at = this.pos;
    this.pos += n;
    return at;
  }

  patchU32(at: number, v: number): void {
    this.view.setUint32(at, v >>> 0, true);
  }
}

export class Reader {
  private view: DataView;
  private buf: Uint8Array;
  private pos: number;
  private end: number;
  /** False once a read ran past the end; generated decoders check it in loops. */
  ok = true;

  constructor(buf: Uint8Array, start = 0, end = buf.length) {
    this.buf = buf;
    this.view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    this.pos = start;
    this.end = end;
  }

  get remaining(): number {
    return this.end - this.pos;
  }

  get offset(): number {
    return this.pos;
  }

  private need(n: number): boolean {
    if (this.pos + n > this.end) {
      this.ok = false;
      this.pos = this.end;
      return false;
    }
    return true;
  }

  u8(): number { return this.need(1) ? this.buf[this.pos++] : 0; }
  i8(): number { if (!this.need(1)) return 0; const v = this.view.getInt8(this.pos); this.pos += 1; return v; }
  u16(): number { if (!this.need(2)) return 0; const v = this.view.getUint16(this.pos, true); this.pos += 2; return v; }
  i16(): number { if (!this.need(2)) return 0; const v = this.view.getInt16(this.pos, true); this.pos += 2; return v; }
  u32(): number { if (!this.need(4)) return 0; const v = this.view.getUint32(this.pos, true); this.pos += 4; return v; }
  i32(): number { if (!this.need(4)) return 0; const v = this.view.getInt32(this.pos, true); this.pos += 4; return v; }
  f32(): number { if (!this.need(4)) return 0; const v = this.view.getFloat32(this.pos, true); this.pos += 4; return v; }
  f64(): number { if (!this.need(8)) return 0; const v = this.view.getFloat64(this.pos, true); this.pos += 8; return v; }

  u64(): bigint {
    if (!this.need(8)) return 0n;
    const v = this.view.getBigUint64(this.pos, true);
    this.pos += 8;
    return v;
  }

  i64(): bigint {
    if (!this.need(8)) return 0n;
    const v = this.view.getBigInt64(this.pos, true);
    this.pos += 8;
    return v;
  }

  bool(): boolean { return this.u8() !== 0; }

  string(): string {
    const len = this.u16();
    if (!this.need(len)) return "";
    const s = textDecoder.decode(this.buf.subarray(this.pos, this.pos + len));
    this.pos += len;
    return s;
  }

  bytes(): Uint8Array {
    const len = this.u32();
    if (!this.need(len)) return new Uint8Array(0);
    const b = this.buf.slice(this.pos, this.pos + len);
    this.pos += len;
    return b;
  }

  uuid(): Uuid {
    if (!this.need(16)) return NULL_UUID;
    const id = uuidFromBytes(this.buf, this.pos);
    this.pos += 16;
    return id;
  }

  vec2(): Vec2 { return { x: this.f32(), y: this.f32() }; }
  vec3(): Vec3 { return { x: this.f32(), y: this.f32(), z: this.f32() }; }
  vec4(): Vec4 { return { x: this.f32(), y: this.f32(), z: this.f32(), w: this.f32() }; }
  quat(): Quat { return unpackQuat(this.u32()); }

  color(): Color {
    return { r: this.u8(), g: this.u8(), b: this.u8(), a: this.u8() };
  }

  enumValue(valid: ReadonlySet<number>, v: number): number {
    if (!valid.has(v)) this.ok = false;
    return v;
  }

  skip(n: number): void {
    this.need(n);
    this.pos += n;
  }
}
